import { MetroStationHeader } from './MetroStationHeader'
import { deriveViewState } from './stationDetailsViewState'
import { FavoritesRow } from './rows/FavoritesRow'
import { FacilitiesRow } from './rows/FacilitiesRow'
import { TimetableRow, type TimetableDirection } from './rows/TimetableRow'
import { TimetableUpsellBanner } from './rows/TimetableUpsellBanner'
import { TransferRow } from './rows/TransferRow'
import { ReportIssueRow } from './rows/ReportIssueRow'
import { MapRow } from './rows/MapRow'
import { StationPageLinkRow } from './rows/StationPageLinkRow'
import { PlainTextHeader } from './rows/PlainTextHeader'
import { OfflineBanner } from './rows/OfflineBanner'
import { Skeleton } from './Skeleton'
import { ErrorState } from './ErrorState'
import { StationDetailsCopy, type StationDetailsCopySet } from './copy'
import styles from './StationDetails.module.css'

export interface StationDetailsProps {
  stationId: string
  stationName: string
  lineLocalName: string
  lineHexColor: string
  zone: string | null
  /** `/<locale>/lines/<slug>` — the line link under the name (Figma LineView E); absent → plain text. */
  lineHref?: string
  /** In-app open of the line view from that link. */
  onLineTap?: () => void
  /**
   * The station's own page (`/<locale>/station/<id>`) — Figma
   * SD/StationDetailsLink (9206:60036) below the map; absent → no row.
   */
  stationPageHref?: string
  /** Analytics hook for that link. */
  onStationPageTap?: () => void
  lat: number
  lng: number
  facilities: ReadonlyArray<string>
  timetable: { directions: ReadonlyArray<TimetableDirection> }
  transfers: ReadonlyArray<{
    id: string
    stationName: string
    lineLocalName: string
    lineHexColor: string
  }>
  isFavorite: boolean
  /** Premium gate — `false` shows the upsell banner; `true` shows the timetable. */
  isPremium: boolean
  /**
   * Whether a renderable station has resolved (iOS `station != nil`).
   * Defaults to `true` (the common case where the host already has the
   * station data it passes as props). Set `false` to drive the
   * loading/error branches.
   */
  hasStation?: boolean
  /** Host station load in flight — drives the S3 skeleton when `!hasStation`. */
  isLoading?: boolean
  /** Reachability — drives the S5 offline banner (host wires `useOnline`). */
  isOffline?: boolean
  onTransferTap: (transferId: string) => void
  onFavoriteTap: () => void
  onPaywallTap: () => void
  onReportIssueTap: () => void
  /** Re-dispatches the station load action from the S4 error Retry CTA. */
  onRetry: () => void
  /**
   * Localised copy set. Defaults to the English {@link StationDetailsCopy};
   * the metro-frontend host injects a catalog-backed set so every label
   * is localised. en values are byte-identical to the default.
   */
  copy?: StationDetailsCopySet
}

/**
 * StationDetails root composition — spec 2026-05-25. Stateless: props
 * in, callbacks out. Renders one of three branches from the derived
 * ViewState (spec §4): S3 skeleton (loading), S4 error+retry (error),
 * or the content body (S1/S2, with the S5 offline banner injected after
 * the header when offline).
 *
 * Row order (spec §5; report is the LAST element in every content state,
 * F1):
 *   Header
 *   [Offline banner]            (S5, after header / before favourites)
 *   Favourites
 *   [Upsell banner]             (!isPremium)
 *   [Transfers header + rows]   (transfers.length > 0)
 *   Exits and map header + map
 *   [Station page link]         (stationPageHref — Figma SD/StationDetailsLink)
 *   [Facilities header + grid]  (facilities.length > 0)
 *   [First & last train + grid] (isPremium && timetable directions)
 *   Divider
 *   Report                      ← last
 *
 * Children supply their own vertical padding; the column has no gap
 * (iOS LazyVStack(spacing: 0)).
 */
export function StationDetails(props: StationDetailsProps): React.ReactElement {
  const {
    hasStation = true,
    isLoading = false,
    isOffline = false,
    isPremium,
    copy = StationDetailsCopy,
  } = props

  const viewState = deriveViewState({ hasStation, isLoading, isOffline })

  if (viewState.kind === 'loading') {
    return (
      <aside data-testid="station-details-view" className={styles.root}>
        <Skeleton loadingLabel={copy.LoadingAria} />
      </aside>
    )
  }

  if (viewState.kind === 'error') {
    return (
      <aside data-testid="station-details-view" className={styles.root}>
        <ErrorState onRetry={props.onRetry} copy={copy} />
      </aside>
    )
  }

  // TODO: web paywall/premium-gating deferred — currently free.
  // `isPremium` is passed straight through from the host (always `false`
  // today on web). When the web entitlement + paywall land, the upsell
  // tap (`onPaywallTap`) must gate premium content behind a paywall
  // surface instead of the host treating premium as free. Until then the
  // upsell renders for everyone and the timetable shows whenever the host
  // passes `isPremium===true` with no purchase step (scenarios doc §0.4).
  const showTimetable = isPremium && props.timetable.directions.length > 0
  const showUpsell = !isPremium

  return (
    <aside data-testid="station-details-view" className={styles.root}>
      {/* 1. Header */}
      <div className={styles.headerSlot}>
        <MetroStationHeader
          title={props.stationName}
          subtitle={props.lineLocalName}
          badge={props.zone}
          lineHexColor={props.lineHexColor}
          zoneLabel={copy.ZoneLabel}
          zoneAria={copy.ZoneAria}
          subtitleHref={props.lineHref}
          onSubtitleTap={props.onLineTap}
        />
      </div>

      {/* 2. Offline banner (S5) — after the header, before favourites. */}
      {viewState.offline && <OfflineBanner label={copy.OfflineBanner} />}

      {/* 3. Favourites */}
      <FavoritesRow
        isFavorite={props.isFavorite}
        onTap={props.onFavoriteTap}
        favoriteLabel={copy.IsFavorite}
        notFavoriteLabel={copy.IsNotFavorite}
      />

      {/* 4. Upsell banner (non-premium) */}
      {showUpsell && <TimetableUpsellBanner onTap={props.onPaywallTap} copy={copy} />}

      {/* 5. Transfers */}
      {props.transfers.length > 0 && (
        <>
          <PlainTextHeader title={copy.Transfers} sectionKey="transfers" />
          {props.transfers.map((t) => (
            <TransferRow
              key={t.id}
              stationId={t.id}
              stationName={t.stationName}
              lineLocalName={t.lineLocalName}
              lineHexColor={t.lineHexColor}
              onTap={() => props.onTransferTap(t.id)}
            />
          ))}
        </>
      )}

      {/* 6. Exits and map */}
      <PlainTextHeader title={copy.ExitsAndMap} sectionKey="exits-and-map" />
      <MapRow stationName={props.stationName} lat={props.lat} lng={props.lng} />
      {props.stationPageHref !== undefined && (
        <StationPageLinkRow
          href={props.stationPageHref}
          label={copy.StationPageLink}
          onTap={props.onStationPageTap}
        />
      )}

      {/* 7. Facilities */}
      {props.facilities.length > 0 && (
        <>
          <PlainTextHeader title={copy.Facilities} sectionKey="facilities" />
          <FacilitiesRow facilities={props.facilities} />
        </>
      )}

      {/* 8. First and last train (premium) — above the divider + report. */}
      {showTimetable && (
        <>
          <PlainTextHeader title={copy.FirstAndLastTrain} sectionKey="first-and-last-train" />
          <TimetableRow timetable={props.timetable} copy={copy} />
        </>
      )}

      {/* 9. Divider */}
      <hr className={styles.divider} />

      {/* 10. Report — LAST element in every content state (F1). */}
      <ReportIssueRow onTap={props.onReportIssueTap} label={copy.ReportIssue} />
    </aside>
  )
}
